import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Menu, Search } from 'lucide-react';
import { getProfileImageUrl } from '../../services/api';
import type { Employee, Location } from '../../types';
import LocationSelect from '../LocationSelect';
import NotificationPanel from '../NotificationPanel';
import MessageInboxButton from '../MessageInboxButton';
import { NavEmoji } from './NavMenu';
import type { ProfileNavEntry } from './navConfig';
import { signOutEntry } from './navConfig';

export interface TopBarDestination {
  label: string;
  href: string;
}

interface AppTopBarProps {
  onOpenMobileMenu: () => void;
  employee: Employee | null;
  roleLabel: string;
  profileItems: ProfileNavEntry[];
  onSignOut: () => void;
  canShowMessages: boolean;
  destinations: TopBarDestination[];
  locations: Location[];
  selectedLocationId: number | null;
  onLocationChange: (locationId: number | null) => void;
  locationDisabled: boolean;
  locationsLoading: boolean;
  locationTitle?: string;
  locationClassName?: string;
}

const AppTopBar: React.FC<AppTopBarProps> = ({
  onOpenMobileMenu,
  employee,
  roleLabel,
  profileItems,
  onSignOut,
  canShowMessages,
  destinations,
  locations,
  selectedLocationId,
  onLocationChange,
  locationDisabled,
  locationsLoading,
  locationTitle,
  locationClassName = '',
}) => {
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const profileRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const displayName = employee
    ? `${employee.firstName} ${employee.lastName}`.trim()
    : 'User';
  const initials = (
    `${employee?.firstName?.[0] ?? ''}${employee?.lastName?.[0] ?? ''}` ||
    employee?.email?.[0] ||
    'U'
  ).toUpperCase();
  const photo = employee?.profileImageUrl
    ? getProfileImageUrl(employee.profileImageUrl, employee.updatedAt)
    : null;

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = destinations.filter((item, index, all) =>
      all.findIndex((other) => other.href === item.href) === index,
    );
    if (!q) return rows.slice(0, 8);
    return rows.filter((item) => item.label.toLowerCase().includes(q)).slice(0, 8);
  }, [destinations, query]);

  useEffect(() => {
    if (!profileOpen && !searchOpen) return;
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (profileOpen && profileRef.current && !profileRef.current.contains(target)) {
        setProfileOpen(false);
      }
      if (searchOpen && searchRef.current && !searchRef.current.contains(target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [profileOpen, searchOpen]);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  return (
    <header className="z-30 flex h-16 shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 sm:px-6">
      <button
        type="button"
        className="inline-flex h-10 w-10 items-center justify-center rounded-full text-slate-600 hover:bg-slate-100 md:hidden"
        onClick={onOpenMobileMenu}
        aria-label="Open menu"
      >
        <Menu className="h-5 w-5" strokeWidth={1.75} />
      </button>

      <div className="ml-auto flex min-w-0 items-center gap-2 sm:gap-3">
        <div className="relative" ref={searchRef}>
          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            aria-label="Search pages"
            title="Search"
            onClick={() => {
              setSearchOpen((open) => !open);
              setQuery('');
            }}
          >
            <Search className="h-5 w-5" strokeWidth={1.75} />
          </button>
          {searchOpen && (
            <div className="absolute right-0 mt-2 w-72 rounded-xl border border-slate-200 bg-white p-2 shadow-xl">
              <input
                ref={searchInputRef}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search pages"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
              />
              <div className="mt-1 max-h-64 overflow-y-auto">
                {results.length === 0 ? (
                  <p className="px-2 py-3 text-sm text-slate-400">No matching pages</p>
                ) : (
                  results.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setSearchOpen(false)}
                      className="block rounded-lg px-2 py-2 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      {item.label}
                    </Link>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {canShowMessages && <MessageInboxButton />}
        <NotificationPanel />

        <LocationSelect
          className={locationClassName}
          locations={locations}
          value={locationDisabled ? null : selectedLocationId}
          onChange={onLocationChange}
          disabled={locationDisabled}
          loading={locationsLoading}
          title={locationTitle}
        />

        <div className="relative" ref={profileRef}>
          <button
            type="button"
            onClick={() => setProfileOpen((open) => !open)}
            className="flex items-center gap-2.5 rounded-lg px-1.5 py-1 hover:bg-slate-50"
          >
            {photo ? (
              <img src={photo} alt="" className="h-9 w-9 rounded-full object-cover" />
            ) : (
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#2F6BFF] text-xs font-semibold text-white">
                {initials}
              </span>
            )}
            <span className="hidden min-w-0 text-left sm:block">
              <span className="block max-w-[160px] truncate text-sm font-semibold leading-tight text-slate-800">
                {displayName}
              </span>
              <span className="block max-w-[160px] truncate text-xs leading-tight text-slate-400">
                {roleLabel}
              </span>
            </span>
            <ChevronDown className="hidden h-4 w-4 text-slate-400 sm:block" strokeWidth={1.75} />
          </button>

          {profileOpen && (
            <div className="absolute right-0 z-50 mt-2 w-72 rounded-xl border border-slate-200 bg-white py-1 shadow-xl">
              {profileItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setProfileOpen(false)}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                >
                  <NavEmoji emoji={item.icon} />
                  <span className="truncate">{item.label}</span>
                </Link>
              ))}
              <hr className="my-1 border-slate-100" />
              <button
                type="button"
                className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                onClick={() => {
                  setProfileOpen(false);
                  onSignOut();
                }}
              >
                <NavEmoji emoji={signOutEntry.icon} />
                {signOutEntry.label}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default AppTopBar;
